import { useState, useEffect, useMemo } from 'react'
import { Plus, Equal } from '@components/icons'
import { Button } from '@corratech/pylot-ui'
import { useTranslation } from 'next-i18next/pages'
import { Checkbox } from '@components/common/Checkbox'
import { formatCurrency } from '@lib/utils/formatCurrency'
import { useAddToCart } from '@corratech/pylot-cart-manager'
import { cartUpdateEvent } from '@lib/utils/exponeaTrackingEvents'
import { ToastType, useUI } from '@corratech/pylot-ui/context'
import { ProductItem } from '@components/common/ProductTile/ProductTile'
import { ProductItemMobile } from '@components/common/ProductTileMobile/ProductTileMobile'
import { useRouter } from 'next/router'
import { priceValueParser } from '@lib/utils/priceValueParser'
import s from './FrequentlyBoughtTogetherProducts.module.scss'
import type { ConfigurableProduct, SimpleProduct } from '@pylot-data/fwrdschema'
import type {
    CartItemPrices,
    CartPrices,
    Maybe,
    PriceRange
} from '@pylot-data/pylotschema'
import { useIsMobileScreen } from '@lib/utils/mediaQuery'
import { useWishlistActions } from '@corratech/pylot-wishlist'
import { useAuthUI, useUser } from '@corratech/pylot-auth-manager'

const TIMEOUT_AFTER_OPENING_SIDEBAR = 300

type Product = SimpleProduct &
    ConfigurableProduct &
    CheckableProduct
export interface FrequentlyBoughtTogetherProducts<ProductType> {
    currentProduct: Product
    currentProductPricing: Maybe<CartPrices | CartItemPrices | PriceRange>
    products: ProductType
    ProductTile: React.ElementType
    imgPreviewSize?: {
        imageLg: number
        imageSm: number
    }
    currentProductSelectedOptionsUIDs: Maybe<Maybe<string>[]> | undefined
}

type CheckableProduct = {
    checked: boolean
    price: {
        regularPrice: {
            amount: {
                value: number
            }
        }
    }
}

type ProductVariantData = {
    id: number
    selected_options: Maybe<Maybe<string>[]>
}

type FBTProductItemProps = {
    product: SimpleProduct | ConfigurableProduct
    handleChangeItemOption: (
        product: SimpleProduct | ConfigurableProduct,
        newSelectedOptions: unknown
    ) => void
    imageParam: { width: string | number }
}

const FBTProductItem = ({
    product,
    handleChangeItemOption,
    imageParam
}: FBTProductItemProps): JSX.Element => {
    const isMobile = useIsMobileScreen()

    return isMobile ? (
        <div key={product.id}>
            <ProductItemMobile
                product={product}
                handleChangeItemOption={handleChangeItemOption}
                isFBT
                imageParam={{ width: 100 }}
            />
        </div>
    ) : (
        <div>
            <ProductItem
                product={product}
                isPDP
                isFBT
                handleChangeItemOption={handleChangeItemOption}
                imageParam={imageParam}
            />
        </div>
    )
}

export const FrequentlyBoughtTogetherProducts = ({
    products,
    imgPreviewSize: { imageLg, imageSm } = { imageLg: 854, imageSm: 405 },
    currentProduct,
    currentProductPricing,
    currentProductSelectedOptionsUIDs
}: FrequentlyBoughtTogetherProducts<
    Maybe<Array<Product>>
>): JSX.Element | null => {
    const { openSidebar, openCartToast } = useUI()
    const { addToCart } = useAddToCart()
    const { asPath } = useRouter()
    const [productsData, setProductsData] = useState<Product[]>([])
    const [currentProductOption, setCurrentProductOption] = useState<
        Maybe<string>[]
    >([])
    const [productVariants, setProductVariants] = useState<
        ProductVariantData[]
    >([])
    const { t } = useTranslation(['pdp', 'common'])
    const { handleAddWishlistAction, isLoading: isAddToWishlistLoading } =
        useWishlistActions()
    const { isSignedIn } = useUser()
    const { openAuthModal } = useAuthUI()

    useEffect(() => {
        if (currentProduct && products?.length && !productsData.length) {
            setProductsData(
                products.map((item) => ({
                    ...item,
                    checked: true
                }))
            )
        }
    }, [products])

    const addProductsHandler = async () => {
        const checkedProducts = productsData
            .filter((item) => item.checked)
            .map((item) => {
                const selectedOptions = productVariants.filter(
                    (e) => e.id == item.id
                )
                if (selectedOptions.length) {
                    return {
                        sku: item.sku as string,
                        uid: `${item.id}`,
                        quantity: 1,
                        selected_options: selectedOptions[0].selected_options
                    }
                } else {
                    return {
                        sku: item.sku as string,
                        uid: `${item.id}`,
                        quantity: 1
                    }
                }
            })

        const response = await addToCart(
            [
                ...checkedProducts,
                {
                    sku: currentProduct.sku as string,
                    quantity: 1,
                    uid: `${currentProduct.id}`,
                    selected_options: currentProductOption.length
                        ? currentProductOption
                        : currentProductSelectedOptionsUIDs
                }
            ],
            []
        )

        // TODO: Workaround for adding multiple FBT products to cart
        await addToCart(
            [
                {
                    sku: currentProduct.sku as string,
                    quantity: 0,
                    uid: `${currentProduct.id}`,
                    selected_options: currentProductOption.length
                        ? currentProductOption
                        : currentProductSelectedOptionsUIDs
                }
            ],
            []
        )

        const errorMessage =
            response?.errors?.[0]?.message ??
            response?.user_errors?.[0]?.message ??
            null
        const successMessage = t('common||cart|{{name}} was added to cart', {
            name: currentProduct.name
        })

        if (response?.data) {
            const { cart } = response.data

            const addedCurrentProduct = {
                ...currentProduct,
                sku: currentProduct.sku,
                uid: `${currentProduct.id}`
            }
            cartUpdateEvent('add', addedCurrentProduct, cart)

            productsData
                .filter((item) => item.checked)
                .map((item) => {
                    const addedProduct = {
                        ...item,
                        sku: item.sku,
                        uid: item.id,
                        price_range: item.price_range
                    }
                    cartUpdateEvent('add', addedProduct, cart)
                })
        }

        setTimeout(
            () =>
                openCartToast(
                    errorMessage ? errorMessage : successMessage,
                    errorMessage ? ToastType.Warning : ToastType.Success
                ),
            TIMEOUT_AFTER_OPENING_SIDEBAR
        )

        openSidebar()
    }

    const handleChangeCheckItems = (product: Product) => {
        const newProductsData = [
            ...productsData.map((item) => {
                if (item.id === product.id) {
                    return {
                        ...item,
                        checked: !item.checked
                    }
                }
                return item
            })
        ]
        setProductsData([...newProductsData])
    }

    const handleChangeItemOption = (
        product: Product,
        newSelectedOptions: any
    ) => {
        if (product.id == currentProduct.id) {
            setCurrentProductOption(newSelectedOptions)
        } else {
            setProductVariants((prevData) => {
                if (prevData.some((e) => e.id == product.id)) {
                    const newProductsData = [
                        ...prevData.map((item) => {
                            if (item.id === product.id) {
                                return {
                                    ...item,
                                    selected_options: newSelectedOptions
                                }
                            }
                            return item
                        })
                    ]
                    return newProductsData
                } else {
                    return [
                        ...prevData,
                        {
                            id: product.id as number,
                            selected_options: newSelectedOptions
                        }
                    ]
                }
            })
        }
    }

    const getTotalValue = () => {
        let totalValue = 0

        const currentProductPrice = priceValueParser(currentProductPricing)
        totalValue += currentProductPrice

        productsData?.forEach((product) => {
            if (product && product.checked) {
                const productPrice = priceValueParser(product.price_range)
                totalValue += productPrice
            }
        })

        return formatCurrency(totalValue)
    }

    const getTotalOfCheckedItems = () => {
        let checkedItemsAmount = 1 // always add the current item to the total amount of checked items

        productsData?.forEach((product) => {
            if (product.checked) {
                checkedItemsAmount += 1
            }
        })

        return checkedItemsAmount
    }

    const getPreviewSize = (index: number) => {
        const preIndex = index + 1
        const postIndex = preIndex
        return `?auto=webp&format=pjpg&width=${
            postIndex % 4 === 0 && postIndex % 12 !== 8 ? imageLg : imageSm
        }`
    }

    const addProductToWishlistHandler = () => {
        if (!products) return

        if (!isSignedIn) {
            openAuthModal()
            return
        }

        for (const product of [currentProduct, ...products]) {
            const selectedOptions =
                product === currentProduct
                    ? currentProductSelectedOptionsUIDs
                    : []

            const updatedCurrentProduct = productsData.find(
                (item) => item.id === product.id
            )

            if (
                updatedCurrentProduct?.checked ||
                currentProduct.id === product.id
            ) {
                handleAddWishlistAction(
                    {
                        wishlistId: 0,
                        itemOptions: [
                            {
                                sku: product.sku as string,
                                quantity: 1,
                                selected_options: selectedOptions
                            }
                        ]
                    },
                    false,
                    true
                )
            }
        }
    }

    const productsList = useMemo(() => {
        return [JSON.parse(JSON.stringify(currentProduct)), ...productsData]
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productsData])

    const productData = useMemo(() => {
        return productsList.map((product, index) => {
            return (
                <>
                    {index > 0 && (
                        <div className={s['product-icon-between-products']}>
                            <Plus />
                        </div>
                    )}
                    <div className={s['product-item']} key={index}>
                        {index > 0 && (
                            <Checkbox
                                className={s['product-checkbox']}
                                checked={!!product.checked}
                                onChange={() => {
                                    handleChangeCheckItems(product)
                                }}
                            />
                        )}
                        <FBTProductItem
                            product={product}
                            imageParam={{
                                width: getPreviewSize(index)
                            }}
                            handleChangeItemOption={handleChangeItemOption}
                        />
                    </div>
                </>
            )
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [products, productsList])

    if (!products?.length) {
        return null
    }

    return (
        <div className={s['frequently-bought-together-products']}>
            <div className={s['heading-container']}>
                <h2 className={s.title}>
                    {t('pdp||Frequently bought together')}
                </h2>
            </div>
            <div className={s['products-list']}>
                {productData}
                <div className={s['product-icon-between-products']}>
                    <Equal />
                </div>
                <div className={s['products-total-container']}>
                    <div className={s['products-total']}>
                        <div className={s['products-total-price-description']}>
                            <span className={s['products-total-price-title']}>
                                {t('pdp||Total Price')}
                            </span>
                            <span className={s['products-total-price']}>
                                {getTotalValue()}
                            </span>
                        </div>
                        <div className={s['products-total-price-actions']}>
                            <Button
                                className={
                                    s[
                                        'products-total-price-actions-shopping-cart'
                                    ]
                                }
                                onClick={addProductsHandler}
                            >
                                {t(
                                    'pdp||Add {{ amount }} products into shopping cart',
                                    { amount: getTotalOfCheckedItems() }
                                )}
                            </Button>
                            <Button
                                className={
                                    s['products-total-price-actions-wishlist']
                                }
                                onClick={addProductToWishlistHandler}
                                variant="secondary"
                                disabled={isAddToWishlistLoading}
                                loading={isAddToWishlistLoading}
                            >
                                {t(
                                    'pdp||Add {{ amount }} products into wish list',
                                    {
                                        amount: getTotalOfCheckedItems()
                                    }
                                )}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
